import { ref, shallowRef } from 'vue';
import { ApiError } from '../http/ApiError.ts';
import { isYourLimit } from '../limits.ts';

export type FieldErrors = Record<string, string>;

/**
 * Submit state for a form: pending flag, a form-level message and per-field
 * messages (mapped from the API's validation details). Ignores re-submits
 * while a request is in flight.
 */
export function useFormSubmit() {
  const pending = ref(false);
  const formError = ref<string | null>(null);
  /** The form error is a limit of your own plan: offer Your plan. */
  const atYourLimit = ref(false);
  const fieldErrors = shallowRef<FieldErrors>({});

  async function submit(action: () => Promise<void>): Promise<boolean> {
    if (pending.value) return false;
    pending.value = true;
    formError.value = null;
    atYourLimit.value = false;
    fieldErrors.value = {};
    try {
      await action();
      return true;
    } catch (err) {
      const { form, fields } = describe(err);
      formError.value = form;
      atYourLimit.value = isYourLimit(err);
      fieldErrors.value = fields;
      return false;
    } finally {
      pending.value = false;
    }
  }

  function reset(): void {
    formError.value = null;
    atYourLimit.value = false;
    fieldErrors.value = {};
  }

  return { pending, formError, atYourLimit, fieldErrors, submit, reset };
}

/** What the browser's passkey prompt says when it doesn't finish. */
const PASSKEY_ERRORS: Record<string, string> = {
  NotAllowedError: 'The passkey prompt was closed or timed out. Try again when you’re ready.',
  AbortError: 'The passkey prompt was closed or timed out. Try again when you’re ready.',
  InvalidStateError: 'This device already has a passkey for your account.',
  SecurityError: 'Passkeys don’t work on this address.',
};

function describe(err: unknown): { form: string | null; fields: FieldErrors } {
  const passkey = err instanceof Error ? PASSKEY_ERRORS[err.name] : undefined;
  if (passkey) return { form: passkey, fields: {} };
  if (!(err instanceof ApiError))
    return { form: 'Something went wrong. Please try again.', fields: {} };
  if (err.isNetworkError)
    return { form: "Can't reach the server. Check your connection.", fields: {} };

  const fields: FieldErrors = {};
  if (err.code === 'validation_error' && Array.isArray(err.details)) {
    for (const detail of err.details as { path?: string; message?: string }[]) {
      const key = detail.path?.split('.')[0];
      if (key && detail.message && !fields[key]) fields[key] = detail.message;
    }
  }
  // Field messages cover the problem; otherwise show the server's message.
  const hasFields = Object.keys(fields).length > 0;
  return { form: hasFields && err.message === 'Invalid request' ? null : err.message, fields };
}
